import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { vendorService, type RecommendAs, type VendorPeopleScope, type VendorRemovalTarget } from '@/services/VendorService'
import { eventKeys, vendorKeys } from '@/hooks/queryKeys'
import type {
  VendorDto,
  VendorListDto,
  VendorVenueListDto,
  VendorServiceApprovalListDto,
  NearbyVenueListDto,
  VendorRequestListDto,
  VendorLinkedVenueListDto,
  VendorSearchResultListDto,
  VendorRemovalPreviewDto,
} from '@lib/dto/vendor.dto'
import type { VendorUserDto, VendorUserListDto } from '@lib/dto/vendor-user.dto'
import type { VendorEventDto, VendorEventListDto } from '@lib/dto/vendor-event.dto'
import type {
  AddVenueVendorInput,
  CreateVendorInput,
  InviteVendorUserInput,
  SetVendorSearchListingInput,
  UpdateVendorInput,
} from '@lib/input/vendor.input'
import type { VendorService } from '@lib/enums/vendor-service.enum'
import type { OwnVendorProfileDto, ProfileChecklistDto, VendorProfileDto } from '@lib/dto/vendor-profile.dto'
import type { VendorDirectoryDto } from '@lib/dto/vendor-directory.dto'
import type { VendorDirectoryQuery } from '@lib/input/vendor-directory.input'
import type {
  AddVendorPhotoInput,
  PutVendorMediaLinksInput,
  ReorderVendorPhotosInput,
  UpdateVendorPhotoInput,
} from '@lib/input/vendor-profile.input'
import type { ReorderVendorOfferingsInput, VendorOfferingInput } from '@lib/input/vendor-offering.input'
import type { VendorPublicPageSettingsDto } from '@lib/dto/vendor-public-page.dto'
import type { UpdateVendorPublicPageInput } from '@lib/input/vendor-public-page.input'

/** All vendors on the platform (super admin only). */
export function useVendors(page?: number): UseQueryResult<VendorListDto> {
  return useQuery({
    queryKey: vendorKeys.list(page),
    queryFn: () => vendorService.list(page),
    staleTime: 60_000,
    // The previous page's rows stay on screen while the next one loads — see
    // the note on `useVenues`.
    placeholderData: (previous) => previous,
  })
}

/** Create a vendor, refreshing the list on success. */
export function useCreateVendor(): UseMutationResult<VendorDto, Error, CreateVendorInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateVendorInput) => vendorService.create(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorKeys.lists() }),
  })
}

/** Rename a vendor, refreshing the list on success. */
export function useUpdateVendor(): UseMutationResult<VendorDto, Error, { id: string; input: UpdateVendorInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateVendorInput }) => vendorService.update(vars.id, vars.input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorKeys.lists() }),
  })
}

/** The signed-in vendor company's own events, across every venue and organization. */
export function useOwnVendorEvents(): UseQueryResult<VendorEventListDto> {
  return useQuery({
    queryKey: vendorKeys.selfEvents(),
    queryFn: () => vendorService.listOwnEvents(),
    staleTime: 30_000,
  })
}

/** One event the signed-in vendor company is on, read-only. */
export function useOwnVendorEvent(eventId: string): UseQueryResult<VendorEventDto> {
  return useQuery({
    queryKey: vendorKeys.selfEvent(eventId),
    queryFn: () => vendorService.getOwnEvent(eventId),
    staleTime: 30_000,
  })
}

/** A vendor's people: any vendor's for a super admin, or the signed-in vendor's own. */
export function useVendorPeople(scope: VendorPeopleScope): UseQueryResult<VendorUserListDto> {
  return useQuery({
    queryKey: vendorKeys.people(scope),
    queryFn: () => vendorService.listPeople(scope),
    staleTime: 30_000,
  })
}

/** After a change to someone, refresh the people, and the vendor list, whose counts change. */
function usePeopleInvalidation(scope: VendorPeopleScope): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: vendorKeys.people(scope) })
    if (scope.kind === 'vendor') void queryClient.invalidateQueries({ queryKey: vendorKeys.lists() })
  }
}

/** Invite someone to a vendor. */
export function useInviteVendorPerson(
  scope: VendorPeopleScope,
): UseMutationResult<VendorUserDto, Error, InviteVendorUserInput> {
  const invalidate = usePeopleInvalidation(scope)
  return useMutation({
    mutationFn: (input: InviteVendorUserInput) => vendorService.invitePerson(scope, input),
    onSuccess: invalidate,
  })
}

/**
 * Withdraw an invitation nobody accepted, removing the person. Offered only for a
 * PENDING invite: deactivating one would leave a row for someone who never
 * arrived and keep hold of their email address, so a mistyped invite could never
 * be re-sent to the corrected one.
 */
export function useDeletePendingVendorPerson(
  scope: VendorPeopleScope,
): UseMutationResult<void, Error, { userId: string }> {
  const invalidate = usePeopleInvalidation(scope)
  return useMutation({
    mutationFn: (vars: { userId: string }) => vendorService.deletePendingPerson(scope, vars.userId),
    onSuccess: invalidate,
  })
}

export function useDeactivateVendorPerson(
  scope: VendorPeopleScope,
): UseMutationResult<VendorUserDto, Error, { userId: string }> {
  const invalidate = usePeopleInvalidation(scope)
  return useMutation({
    mutationFn: (vars: { userId: string }) => vendorService.deactivatePerson(scope, vars.userId),
    onSuccess: invalidate,
  })
}

export function useReactivateVendorPerson(
  scope: VendorPeopleScope,
): UseMutationResult<VendorUserDto, Error, { userId: string }> {
  const invalidate = usePeopleInvalidation(scope)
  return useMutation({
    mutationFn: (vars: { userId: string }) => vendorService.reactivatePerson(scope, vars.userId),
    onSuccess: invalidate,
  })
}

/** Send a pending person's invitation again. */
export function useResendVendorPersonInvite(
  scope: VendorPeopleScope,
): UseMutationResult<void, Error, { userId: string }> {
  return useMutation({
    mutationFn: (vars: { userId: string }) => vendorService.resendPersonInvite(scope, vars.userId),
  })
}

/** Correct a pending owner's email and invite them again (super admin). */
export function useCorrectVendorPersonEmail(
  vendorId: string,
): UseMutationResult<void, Error, { userId: string; email: string }> {
  const invalidate = usePeopleInvalidation({ kind: 'vendor', vendorId })
  return useMutation({
    mutationFn: (vars: { userId: string; email: string }) =>
      vendorService.correctPendingEmail(vendorId, vars.userId, vars.email),
    onSuccess: invalidate,
  })
}

/** Make someone an owner, or stop them being one. */
export function useSetVendorPersonOwner(
  scope: VendorPeopleScope,
): UseMutationResult<VendorUserDto, Error, { userId: string; isOwner: boolean }> {
  const invalidate = usePeopleInvalidation(scope)
  return useMutation({
    mutationFn: (vars: { userId: string; isOwner: boolean }) =>
      vendorService.setPersonOwner(scope, vars.userId, vars.isOwner),
    onSuccess: invalidate,
  })
}

/** Every venue (across tenants) with a linked flag — the super admin's picker. */
export function useVendorVenues(vendorId: string): UseQueryResult<VendorVenueListDto> {
  return useQuery({
    queryKey: vendorKeys.venues(vendorId),
    queryFn: () => vendorService.listVenues(vendorId),
    staleTime: 30_000,
  })
}

/** Link a vendor to a venue; refreshes that vendor's venue list + the vendor list. */
export function useLinkVendorVenue(vendorId: string): UseMutationResult<void, Error, { venueId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string }) => vendorService.linkVenue(vendorId, vars.venueId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorKeys.venues(vendorId) }),
  })
}

/** Unlink a vendor from a venue, taking it off its upcoming events there. */
export function useUnlinkVendorVenue(vendorId: string): UseMutationResult<void, Error, { venueId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { venueId: string }) => vendorService.unlinkVenue(vendorId, vars.venueId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.venues(vendorId) })
      void queryClient.invalidateQueries({ queryKey: eventKeys.all })
    },
  })
}

/** What stopping work with a company would do. Always asked again: the warning must be current. */
export function useVendorRemovalPreview(
  target: VendorRemovalTarget,
  enabled: boolean,
): UseQueryResult<VendorRemovalPreviewDto> {
  return useQuery({
    queryKey: vendorKeys.removal(target),
    queryFn: () => vendorService.removalPreview(target),
    enabled,
    staleTime: 0,
    gcTime: 0,
  })
}

/** A venue stops working with a company; the answer replaces the venue's vendors. */
export function useStopVenueVendor(venueId: string): UseMutationResult<VendorServiceApprovalListDto, Error, { vendorId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ vendorId }: { vendorId: string }) => vendorService.stopAtVenue(venueId, vendorId),
    onSuccess: (list) => {
      queryClient.setQueryData(vendorKeys.venueServices(venueId), list)
      void queryClient.invalidateQueries({ queryKey: eventKeys.all })
    },
  })
}

/** Deactivate a company (super admin); its people, venues and events all change. */
export function useDeactivateVendor(): UseMutationResult<VendorDto, Error, { vendorId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ vendorId }: { vendorId: string }) => vendorService.deactivate(vendorId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all })
      void queryClient.invalidateQueries({ queryKey: eventKeys.all })
    },
  })
}

/** The venues the signed-in vendor user's vendor is linked to (read-only). */
export function useOwnVendorVenues(): UseQueryResult<VendorLinkedVenueListDto> {
  return useQuery({
    queryKey: vendorKeys.selfVenues(),
    queryFn: () => vendorService.getOwnVenues(),
    staleTime: 60_000,
  })
}

/** The single vendor the signed-in vendor user belongs to. `enabled` lets a
 * non-vendor caller (e.g. the layout deciding nav) skip the fetch. */
export function useOwnVendor(options?: { enabled?: boolean }): UseQueryResult<VendorDto> {
  return useQuery({
    queryKey: vendorKeys.self(),
    queryFn: () => vendorService.getOwn(),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Every cached profile: the company's own for its editor (`vendorKeys.profile`)
 * and the read-only ones (`vendorKeys.profileOf`), both keyed `['vendors', …,
 * 'profile']`. Anything that changes a company changes what those show.
 */
const isProfileKey = (queryKey: QueryKey): boolean => queryKey[0] === vendorKeys.all[0] && queryKey[2] === 'profile'

function refreshProfiles(queryClient: QueryClient, keepOwn = false): void {
  void queryClient.invalidateQueries({
    predicate: (query) => isProfileKey(query.queryKey) && !(keepOwn && query.queryKey[1] === 'self'),
  })
}

/** Change the signed-in vendor's own company: its name and address, or its profile's text. */
export function useUpdateOwnVendor(): UseMutationResult<VendorDto, Error, UpdateVendorInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateVendorInput) => vendorService.updateOwn(input),
    onSuccess: (vendor) => {
      queryClient.setQueryData(vendorKeys.self(), vendor)
      // The profile shows the same company: its logo, description and contacts.
      refreshProfiles(queryClient)
    },
  })
}

/** A company's profile, as venue staff and planners read it; nothing is fetched until there is one to show. */
export function useVendorProfile(vendorId: string | null): UseQueryResult<VendorProfileDto> {
  return useQuery({
    queryKey: vendorKeys.profileOf(vendorId ?? ''),
    queryFn: () => vendorService.getProfile(vendorId ?? ''),
    enabled: vendorId !== null,
    staleTime: 60_000,
  })
}

/** After a recommendation changes: the profile that names it, and a venue's list that marks it. */
function useRecommendationRefresh(): (by: RecommendAs, vendorId: string) => void {
  const queryClient = useQueryClient()
  return (by, vendorId) => {
    void queryClient.invalidateQueries({ queryKey: vendorKeys.profileOf(vendorId) })
    if (by.as === 'venue') void queryClient.invalidateQueries({ queryKey: vendorKeys.venueServices(by.venueId) })
  }
}

/** Recommend a company, as a venue or as the signed-in planning company. */
export function useRecommendVendor(): UseMutationResult<void, Error, { by: RecommendAs; vendorId: string }> {
  const refresh = useRecommendationRefresh()
  return useMutation({
    mutationFn: ({ by, vendorId }) => vendorService.recommend(by, vendorId),
    onSuccess: (_, { by, vendorId }) => refresh(by, vendorId),
  })
}

export function useWithdrawRecommendation(): UseMutationResult<void, Error, { by: RecommendAs; vendorId: string }> {
  const refresh = useRecommendationRefresh()
  return useMutation({
    mutationFn: ({ by, vendorId }) => vendorService.withdrawRecommendation(by, vendorId),
    onSuccess: (_, { by, vendorId }) => refresh(by, vendorId),
  })
}

/** The venue's vendors, as an event's couple or planner browses them (docs/vendor-growth.spec.md, "Couples find vendors"). */
export function useVendorDirectory(eventId: string, query: VendorDirectoryQuery): UseQueryResult<VendorDirectoryDto> {
  return useQuery({
    queryKey: vendorKeys.directory(eventId, query),
    queryFn: () => vendorService.vendorDirectory(eventId, query),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
}

/** The signed-in vendor's own profile, for its editor (docs/vendor-growth.spec.md). */
export function useOwnVendorProfile(): UseQueryResult<OwnVendorProfileDto> {
  return useQuery({
    queryKey: vendorKeys.profile(),
    queryFn: () => vendorService.getOwnProfile(),
    staleTime: 60_000,
  })
}

/**
 * What the company's profile still needs (docs/vendor-growth.spec.md, "First
 * sign-in"). Asked again on every page that shows it, so a piece added to the
 * furniture catalog elsewhere is counted the next time it is seen.
 */
export function useProfileChecklist(): UseQueryResult<ProfileChecklistDto> {
  return useQuery({ queryKey: vendorKeys.checklist(), queryFn: () => vendorService.getChecklist() })
}

/**
 * One change to the profile's gallery, links or packages. Every answer is the
 * whole profile, which replaces the cached one.
 */
function useProfileWrite<V>(write: (variables: V) => Promise<OwnVendorProfileDto>): UseMutationResult<OwnVendorProfileDto, Error, V> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: write,
    onSuccess: (profile) => {
      queryClient.setQueryData(vendorKeys.profile(), profile)
      // Its read-only copy, if the company has looked at itself from an event.
      refreshProfiles(queryClient, true)
      void queryClient.invalidateQueries({ queryKey: vendorKeys.checklist() })
      void queryClient.invalidateQueries({ queryKey: vendorKeys.publicPage() })
    },
  })
}

/** The signed-in vendor company's page on csvenueview.com: whether it is on, its address, and why it may not show. */
export function useVendorPublicPage(): UseQueryResult<VendorPublicPageSettingsDto> {
  return useQuery({ queryKey: vendorKeys.publicPage(), queryFn: () => vendorService.getPublicPage() })
}

/** Publish or unpublish it (an owner only). */
export function useUpdateVendorPublicPage(): UseMutationResult<VendorPublicPageSettingsDto, Error, UpdateVendorPublicPageInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateVendorPublicPageInput) => vendorService.updatePublicPage(input),
    onSuccess: (settings) => queryClient.setQueryData(vendorKeys.publicPage(), settings),
  })
}

export const useAddOwnVendorPhoto = () => useProfileWrite((input: AddVendorPhotoInput) => vendorService.addOwnPhoto(input))
export const useUpdateOwnVendorPhoto = () =>
  useProfileWrite(({ photoId, input }: { photoId: string; input: UpdateVendorPhotoInput }) => vendorService.updateOwnPhoto(photoId, input))
export const useDeleteOwnVendorPhoto = () => useProfileWrite((photoId: string) => vendorService.deleteOwnPhoto(photoId))
export const useReorderOwnVendorPhotos = () => useProfileWrite((input: ReorderVendorPhotosInput) => vendorService.reorderOwnPhotos(input))
export const usePutOwnMediaLinks = () => useProfileWrite((input: PutVendorMediaLinksInput) => vendorService.putOwnMediaLinks(input))
export const useCreateOwnOffering = () => useProfileWrite((input: VendorOfferingInput) => vendorService.createOwnOffering(input))
export const useReplaceOwnOffering = () =>
  useProfileWrite(({ offeringId, input }: { offeringId: string; input: VendorOfferingInput }) => vendorService.replaceOwnOffering(offeringId, input))
export const useDeleteOwnOffering = () => useProfileWrite((offeringId: string) => vendorService.deleteOwnOffering(offeringId))
export const useReorderOwnOfferings = () => useProfileWrite((input: ReorderVendorOfferingsInput) => vendorService.reorderOwnOfferings(input))

/** Whether venues can find the signed-in user's company in search (owners only). */
export function useSetOwnVendorSearchListing(): UseMutationResult<VendorDto, Error, SetVendorSearchListingInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SetVendorSearchListingInput) => vendorService.setOwnSearchListing(input),
    onSuccess: (vendor) => queryClient.setQueryData(vendorKeys.self(), vendor),
  })
}

/** Set the services the signed-in vendor offers (self-service). */
export function useSetOwnVendorServices(): UseMutationResult<VendorDto, Error, VendorService[]> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (services: VendorService[]) => vendorService.setOwnServices(services),
    onSuccess: (vendor) => {
      queryClient.setQueryData(vendorKeys.self(), vendor)
      // The profile's sections are its services: a new one needs its section now.
      refreshProfiles(queryClient)
    },
  })
}

/** Super-admin override of a vendor's offered services. */
export function useSetVendorServices(): UseMutationResult<VendorDto, Error, { id: string; services: VendorService[] }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, services }: { id: string; services: VendorService[] }) => vendorService.setServices(id, services),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.lists() })
      refreshProfiles(queryClient)
    },
  })
}

/** The vendors associated with a venue and their offered/approved services. */
export function useVenueVendorServices(venueId: string): UseQueryResult<VendorServiceApprovalListDto> {
  return useQuery({
    queryKey: vendorKeys.venueServices(venueId),
    queryFn: () => vendorService.listVenueVendorServices(venueId),
    staleTime: 60_000,
  })
}

/**
 * Every company on the platform whose name matches, for a venue's manager to add.
 * Disabled until something is typed: an empty search would list companies in
 * alphabetical order, which answers no question anybody asked.
 */
export function useVendorSearch(
  venueId: string,
  q: string,
  service?: VendorService,
): UseQueryResult<VendorSearchResultListDto> {
  const term = q.trim()
  return useQuery({
    queryKey: vendorKeys.search(venueId, term, service),
    queryFn: () => vendorService.searchForVenue(venueId, { q: term, ...(service !== undefined ? { service } : {}) }),
    enabled: term.length > 0,
    staleTime: 15_000,
  })
}

/** Set a company up at a venue; the answer is the venue's vendors, so it replaces them. */
export function useAddVenueVendor(venueId: string): UseMutationResult<VendorServiceApprovalListDto, Error, AddVenueVendorInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AddVenueVendorInput) => vendorService.addToVenue(venueId, input),
    onSuccess: (list) => {
      queryClient.setQueryData(vendorKeys.venueServices(venueId), list)
      // A search result's "already here" is now out of date.
      void queryClient.invalidateQueries({ queryKey: [...vendorKeys.all, 'venue', venueId, 'search'] })
    },
  })
}

/** Correct a pending owner's email, or send their invitation again to the same one. */
export function useCorrectVendorOwnerEmail(
  venueId: string,
): UseMutationResult<void, Error, { vendorId: string; email: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ vendorId, email }: { vendorId: string; email: string }) =>
      vendorService.correctOwnerEmail(venueId, vendorId, email),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorKeys.venueServices(venueId) }),
  })
}

/** Venues near the signed-in vendor (discovery). */
export function useNearbyVenues(): UseQueryResult<NearbyVenueListDto> {
  return useQuery({
    queryKey: vendorKeys.selfNearby(),
    queryFn: () => vendorService.getNearbyVenues(),
    staleTime: 60_000,
  })
}

/** Request to serve a venue; refreshes the discovery list. */
export function useRequestVenue(): UseMutationResult<void, Error, { venueId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ venueId }: { venueId: string }) => vendorService.requestVenue(venueId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorKeys.selfNearby() }),
  })
}

/** Pending vendor requests for a venue (tenant admin). */
export function useVenueVendorRequests(venueId: string): UseQueryResult<VendorRequestListDto> {
  return useQuery({
    queryKey: vendorKeys.venueRequests(venueId),
    queryFn: () => vendorService.listVenueRequests(venueId),
    staleTime: 60_000,
  })
}

/** Accept a vendor's request — it becomes an active association (approvable). */
export function useAcceptVenueRequest(venueId: string): UseMutationResult<void, Error, { vendorId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ vendorId }: { vendorId: string }) => vendorService.acceptVenueRequest(venueId, vendorId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.venueRequests(venueId) })
      void queryClient.invalidateQueries({ queryKey: vendorKeys.venueServices(venueId) })
    },
  })
}

/** Decline (remove) a vendor's request. */
export function useDeclineVenueRequest(venueId: string): UseMutationResult<void, Error, { vendorId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ vendorId }: { vendorId: string }) => vendorService.declineVenueRequest(venueId, vendorId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorKeys.venueRequests(venueId) }),
  })
}

/**
 * Replace the venue's approved services for one vendor.
 *
 * The list is the whole set, and the screen builds the next set from what it
 * is showing. The cache is updated before the request returns, so a second
 * click is not computed from the set that was there before the first one.
 * A refusal puts that set back.
 */
export function useSetVenueVendorServices(
  venueId: string,
): UseMutationResult<void, Error, { vendorId: string; services: VendorService[] }> {
  const queryClient = useQueryClient()
  const key = vendorKeys.venueServices(venueId)
  return useMutation({
    mutationFn: ({ vendorId, services }: { vendorId: string; services: VendorService[] }) =>
      vendorService.setVenueVendorServices(venueId, vendorId, services),
    onMutate: async ({ vendorId, services }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<VendorServiceApprovalListDto>(key)
      queryClient.setQueryData<VendorServiceApprovalListDto>(key, (current) => {
        if (!current) return current
        return {
          ...current,
          vendors: current.vendors.map((vendor) =>
            vendor.vendorId === vendorId ? { ...vendor, approved: services } : vendor,
          ),
        }
      })
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous === undefined) return
      queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
